// Super Admin: Manage the pool of survey questions used to build surveys.

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  query,
  orderBy,
  getDocs,
  doc,
  updateDoc,
  addDoc,
  deleteDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/authContext';
import Layout from '../Layout';
import { toast } from 'react-hot-toast';
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import { SURVEY_QUESTIONS_SEED } from '../../data/surveyQuestionsSeed';

const SurveyQuestionsManagement = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionText, setQuestionText] = useState('');
  const [category, setCategory] = useState('');
  const [bulkPaste, setBulkPaste] = useState('');
  const [bulkAdding, setBulkAdding] = useState(false);

  useEffect(() => {
    if (isSuperAdmin()) {
      loadQuestions();
    }
  }, []);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      let snapshot;
      try {
        const q = query(
          collection(db, 'surveyQuestions'),
          orderBy('order', 'asc')
        );
        snapshot = await getDocs(q);
      } catch (indexErr) {
        snapshot = await getDocs(collection(db, 'surveyQuestions'));
      }
      const list = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
      setQuestions(list);
    } catch (err) {
      console.error('Error loading survey questions:', err);
      toast.error('Failed to load questions');
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingQuestion(null);
    setQuestionText('');
    setCategory('');
    setShowModal(true);
  };

  const handleEdit = (q) => {
    setEditingQuestion(q);
    setQuestionText(q.text || '');
    setCategory(q.category || '');
    setShowModal(true);
  };

  const handleSave = async () => {
    const text = questionText.trim();
    if (!text) {
      toast.error('Question text is required');
      return;
    }
    try {
      const data = {
        text,
        category: category.trim() || null,
        updatedAt: serverTimestamp(),
      };
      if (editingQuestion) {
        await updateDoc(doc(db, 'surveyQuestions', editingQuestion.id), data);
        toast.success('Question updated');
      } else {
        const maxOrder = questions.length
          ? Math.max(...questions.map((q) => q.order ?? 0))
          : -1;
        await addDoc(collection(db, 'surveyQuestions'), {
          ...data,
          order: maxOrder + 1,
          createdAt: serverTimestamp(),
        });
        toast.success('Question added');
      }
      setShowModal(false);
      setEditingQuestion(null);
      setQuestionText('');
      setCategory('');
      loadQuestions();
    } catch (err) {
      console.error('Error saving question:', err);
      toast.error('Failed to save question');
    }
  };

  const handleDelete = async (q) => {
    if (!window.confirm('Delete this question? It may be used in surveys.')) return;
    try {
      await deleteDoc(doc(db, 'surveyQuestions', q.id));
      toast.success('Question deleted');
      loadQuestions();
    } catch (err) {
      console.error('Error deleting question:', err);
      toast.error('Failed to delete question');
    }
  };

  const existingTexts = new Set(questions.map((q) => (q.text || '').trim().toLowerCase()));

  const handleLoadSeed = async () => {
    if (!window.confirm(`Add ${SURVEY_QUESTIONS_SEED.length} curated questions to the pool? (Questions that already exist will be skipped.)`)) return;
    setBulkAdding(true);
    try {
      const toAdd = SURVEY_QUESTIONS_SEED.filter(
        (text) => text && !existingTexts.has(text.trim().toLowerCase())
      );
      if (toAdd.length === 0) {
        toast.success('All seed questions are already in the pool.');
        setBulkAdding(false);
        return;
      }
      let maxOrder = questions.length ? Math.max(...questions.map((q) => q.order ?? 0)) : -1;
      const ref = collection(db, 'surveyQuestions');
      for (const text of toAdd) {
        maxOrder += 1;
        await addDoc(ref, {
          text: text.trim(),
          category: null,
          order: maxOrder,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      toast.success(`Added ${toAdd.length} questions to the pool.`);
      setBulkAdding(false);
      loadQuestions();
    } catch (err) {
      console.error('Error loading seed:', err);
      toast.error('Failed to add seed questions');
      setBulkAdding(false);
    }
  };

  const handleBulkAddFromPaste = async () => {
    const lines = bulkPaste
      .split(/\n/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (lines.length === 0) {
      toast.error('Paste at least one question (one per line).');
      return;
    }
    if (!window.confirm(`Add ${lines.length} question(s) to the pool? Duplicates will be skipped.`)) return;
    setBulkAdding(true);
    try {
      const toAdd = lines.filter((text) => !existingTexts.has(text.toLowerCase()));
      if (toAdd.length === 0) {
        toast.success('All pasted questions already exist in the pool.');
        setBulkPaste('');
        setBulkAdding(false);
        return;
      }
      let maxOrder = questions.length ? Math.max(...questions.map((q) => q.order ?? 0)) : -1;
      const ref = collection(db, 'surveyQuestions');
      for (const text of toAdd) {
        maxOrder += 1;
        await addDoc(ref, {
          text,
          category: null,
          order: maxOrder,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      toast.success(`Added ${toAdd.length} question(s) to the pool.`);
      setBulkPaste('');
      setBulkAdding(false);
      loadQuestions();
    } catch (err) {
      console.error('Error bulk adding:', err);
      toast.error('Failed to add questions');
      setBulkAdding(false);
    }
  };

  if (!isSuperAdmin()) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-red-600">Access Denied</h2>
            <p className="mt-2 text-gray-600">You don&apos;t have permission to access this area.</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <button
            onClick={() => navigate('/app-admin')}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Admin
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Survey Question Pool</h1>
          <p className="mt-2 text-gray-600">
            Add and edit questions that can be used when building surveys. These are available to all surveys.
          </p>
        </div>

        <div className="mb-6 flex flex-wrap gap-4">
          <button
            onClick={handleAdd}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Question
          </button>
          <button
            onClick={handleLoadSeed}
            disabled={bulkAdding}
            className="inline-flex items-center px-4 py-2 border border-amber-300 text-sm font-medium rounded-md text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-50"
          >
            <DocumentDuplicateIcon className="h-5 w-5 mr-2" />
            Add curated seed list ({SURVEY_QUESTIONS_SEED.length} questions)
          </button>
        </div>

        <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Bulk add from paste (one question per line)</h3>
          <textarea
            value={bulkPaste}
            onChange={(e) => setBulkPaste(e.target.value)}
            rows={4}
            placeholder="Paste your list here..."
            className="mb-2 w-full rounded border border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          />
          <button
            onClick={handleBulkAddFromPaste}
            disabled={bulkAdding || !bulkPaste.trim()}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            {bulkAdding ? 'Adding...' : 'Add to pool'}
          </button>
        </div>

        {loading && questions.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            <p className="mt-4 text-gray-600">Loading questions...</p>
          </div>
        ) : questions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-600">No questions yet. Add questions above to build the pool.</p>
          </div>
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {questions.map((q, idx) => (
                <li key={q.id} className="px-6 py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-500">#{idx + 1}</span>
                        {q.category && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                            {q.category}
                          </span>
                        )}
                      </div>
                      <p className="mt-1 text-gray-900">{q.text}</p>
                    </div>
                    <div className="ml-4 flex items-center space-x-2">
                      <button
                        onClick={() => handleEdit(q)}
                        className="inline-flex items-center p-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        aria-label="Edit"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(q)}
                        className="inline-flex items-center p-2 border border-gray-300 rounded-md text-red-700 bg-white hover:bg-red-50"
                        aria-label="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-white">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {editingQuestion ? 'Edit Question' : 'Add Question'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Question text</label>
                  <textarea
                    value={questionText}
                    onChange={(e) => setQuestionText(e.target.value)}
                    rows={3}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="e.g. What do you think happens after we die?"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category (optional)</label>
                  <input
                    type="text"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                    placeholder="e.g. Exploring Faith, Identity"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowModal(false);
                    setEditingQuestion(null);
                    setQuestionText('');
                    setCategory('');
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  {editingQuestion ? 'Update' : 'Add'} Question
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SurveyQuestionsManagement;
